import React from "react";
import { Link } from "react-router-dom";
import { MdArrowBack } from "react-icons/md";
import Navbar from "layouts/Navbar";
import Footer from "layouts/Footer";

const Privacy = () => {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-white/80 via-violet-50/30 to-purple-50/20 pt-20">
        <div className="mx-auto max-w-4xl px-4 py-12">
          <Link to="/" className="mb-8 inline-flex items-center text-purple-800 hover:text-purple-600">
            <MdArrowBack className="mr-2" />
            Back to Home
          </Link>

          <div className="rounded-xl bg-white/80 p-8 shadow-lg backdrop-blur-sm">
            <h1 className="mb-6 text-3xl font-bold text-gray-900">Riplestack Inc. Privacy Policy</h1>
            <p className="mb-8 text-sm text-gray-600">Last updated: 21/02/2025</p>

            <div className="prose prose-purple max-w-none">
              <p className="mb-6">
                Riplestack Inc. ("Riplestack", "we", "us" or "our") respects your privacy and is committed to
                protecting your personal data. This Privacy Policy explains who we are, how we collect,
                share, and use information about you, and your rights regarding your personal data when
                you access and use our website (<a href="https://www.riplestack.com" className="text-purple-600 hover:text-purple-700">www.riplestack.com</a>) and related services (collectively, the
                "Service").
              </p>

              <h2 className="mt-8 text-2xl font-bold">1. Who We Are</h2>
              <p>
                Riplestack Inc. is a provider of innovative social media management solutions designed for
                businesses of all sizes. Our Service enables you to create, schedule, publish, and analyze
                social media content across multiple channels. For more about us, please visit our About Us
                page. If you have any questions or concerns about how we use your information, please contact us
                using the details provided in Section 12.
              </p>

              <h2 className="mt-8 text-2xl font-bold">2. Information We Collect</h2>
              <p>
                We collect information from and about you from various sources to provide and improve our
                Service. The information we collect includes:
              </p>

              <h3 className="mt-6 text-base font-semibold text-gray-700">A. Information You Provide Directly</h3>
              <ul className="list-disc pl-6">
                <li><strong>Account Registration:</strong> When you create an account, we collect your name, email
                  address, phone number, business details, and payment information.</li>
                <li><strong>Customer Support and Feedback:</strong> If you contact us for support or provide feedback
                  via our website or email, we may collect the content of your communications.</li>
                <li><strong>Job Applications:</strong> If you apply for a position with Riplestack, we may collect
                  information such as your resume, employment history, and contact details.</li>
                <li><strong>Content Submission:</strong> Any data, including text, images, videos, and other materials
                  you upload or input into our platform (collectively "Customer Content").</li>
              </ul>

              <h3 className="mt-6 text-base font-semibold text-gray-700">B. Information Collected Automatically</h3>
              <p>
                When you use our Service, we automatically collect technical and usage information,
                including:
              </p>
              <ul className="list-disc pl-6">
                <li><strong>Device and Log Data:</strong> IP address, browser type, operating system, device
                  identifiers, and other technical data.</li>
                <li><strong>Usage Information:</strong> Details of your interactions with our website and Service (e.g.,
                  pages visited, features used, session duration, and clicks).</li>
                <li><strong>Cookies and Similar Technologies:</strong> We use cookies, web beacons, and other
                  tracking technologies to collect information about your browsing activities. For more
                  details, please review our Cookie Notice.</li>
              </ul>

              <h3 className="mt-6 text-base font-semibold text-gray-700">C. Information from Third Parties</h3>
              <p>
                We may receive information about you from third-party sources, including:
              </p>
              <ul className="list-disc pl-6">
                <li><strong>Social Networks:</strong> If you connect your social media accounts or interact with our
                  content on third-party platforms, we may receive data such as your social media
                  handle, profile picture, and public posts.</li>
                <li><strong>Partners and Data Providers:</strong> We may supplement the data we collect with
                  information from partners, data brokers, or publicly available sources to verify or
                  enhance your profile.</li>
              </ul>

              <h2 className="mt-8 text-2xl font-bold">3. How We Use Your Information</h2>
              <p>
                We use your information for business and operational purposes, including to:
              </p>
              <li><strong>Provide and Improve Our Service:</strong> Operate, maintain, and enhance our platform
                and features.</li>
              <li><strong>Account Management:</strong> Process transactions, manage user accounts, and support
                customer inquiries.</li>
              <li><strong>Communication:</strong> Send you updates, newsletters, marketing materials, and
                notifications relevant to our Service. You may opt out of marketing communications at
                any time.</li>
              <li><strong>Analytics and Research:</strong> Analyze user behavior and trends to optimize our platform
                and deliver personalized content.</li>
              <li><strong>Compliance:</strong> Meet legal, regulatory, and contractual obligations.</li>
              <p>
                We may also use aggregated or de-identified information for any purpose not prohibited by
                law.
              </p>

              <h2 className="mt-8 text-2xl font-bold">4. How We Share Your Information</h2>
              <p>
                Your information may be shared with:
              </p>
              <ul className="list-disc pl-6">
                <li>
                  <strong>Service Providers and Affiliates:</strong> Trusted third parties that perform services on our
                  behalf (e.g., payment processors, hosting providers, analytics and advertising
                  partners). These providers are contractually bound to maintain confidentiality and use
                  your data only for the purposes specified by us.
                </li>
                <li>
                  <strong>Business Partners:</strong> For joint marketing or co-branded services, subject to your
                  consent where required.
                </li>
                <li>
                  <strong>Legal and Regulatory Authorities:</strong> When required by law or in response to lawful
                  requests from public authorities.
                </li>
                <li>
                  <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets,
                  provided that the recipient agrees to maintain the confidentiality of your information.
                </li>
                <li>
                  <strong>Third-Party Integrations:</strong> When you enable integrations with social networks or other 
                  third-party services, your data may be shared in accordance with their respective privacy policies.
                </li>
              </ul>
              <p className="mt-4">
                We do not sell your personal data to third parties.
              </p>

              <h2 className="mt-8 text-2xl font-bold">5. Data Retention</h2>
              <p>
                We retain your personal information only as long as necessary for the purposes for which it was 
                collected or as required by law. When no longer needed, your data will be deleted or anonymized 
                in accordance with our data retention practices.
              </p>

              <h2 className="mt-8 text-2xl font-bold">6. Data Security</h2>
              <p>
                We implement industry-standard administrative, technical, and physical security measures to 
                safeguard your personal information. While we strive to protect your data, no method of 
                transmission over the Internet or electronic storage is completely secure.
              </p>

              <h2 className="mt-8 text-2xl font-bold">7. Your Data Protection Rights</h2>
              <p>
                Depending on your jurisdiction, you may have the following rights regarding your personal data:
              </p>
              <ul className="list-disc pl-6">
                <li>
                  <strong>Access and Correction:</strong> Request access to and correction of your personal data.
                </li>
                <li>
                  <strong>Deletion:</strong> Request the deletion of your personal information, subject to legal 
                  or contractual restrictions.
                </li>
                <li>
                  <strong>Objection and Restriction:</strong> Object to or request restrictions on the processing 
                  of your data.
                </li>
                <li>
                  <strong>Data Portability:</strong> Request a copy of your personal data in a structured, 
                  commonly used format.
                </li>
                <li>
                  <strong>Opt-Out:</strong> Withdraw consent for processing or opt out of receiving marketing 
                  communications.
                </li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us using the details in Section 12. Note that 
                exercising certain rights may affect your ability to use our Service.
              </p>

              <h2 className="mt-8 text-2xl font-bold">8. International Data Transfers</h2>
              <p>
                Your personal information may be transferred to and processed in countries outside your home 
                jurisdiction. We take appropriate safeguards, such as Standard Contractual Clauses, to ensure 
                that your data remains protected in accordance with this Privacy Policy and applicable law.
              </p>

              <h2 className="mt-8 text-2xl font-bold">9. Cookies, Tracking Technologies, and Analytics</h2>
              <p>
                We use cookies and similar tracking technologies to collect information about your use of our 
                Service and to enhance your experience. For more detailed information about the cookies we use 
                and how to control them, please refer to our Cookie Notice.
              </p>

              <h2 className="mt-8 text-2xl font-bold">10. Children's Privacy</h2>
              <p>
                Our Service is not directed to children under 13 years of age. We do not knowingly collect 
                personal information from children under 13. If you believe that we have inadvertently 
                collected such information, please contact us immediately so that we can take appropriate steps.
              </p>

              <h2 className="mt-8 text-2xl font-bold">11. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices, legal 
                requirements, or business operations. The "Last updated" date at the top of this Privacy Policy 
                indicates the most recent update. Your continued use of our Service after any changes are 
                posted constitutes your acceptance of the revised Privacy Policy.
              </p>

              <h2 className="mt-8 text-2xl font-bold">12. How to Contact Us</h2>
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy
                practices, please contact us at:
              </p>
              <p className="mt-2">
                <strong>Email:</strong>{" "}
                <a href="mailto:contact@riplstack.com" className="text-purple-600 hover:text-purple-700">
                  contact@riplstack.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Privacy;
